/**
 * @fileoverview A reusable dropdown component that handles selection and displays options.
 * The component includes a label, a placeholder, and an optional hint for additional information.
 * It supports error handling and provides a consistent styling for the dropdown.
 *
 * @example
 * <Dropdown
 *   label="Select an option"
 *   name="option"
 *   options={[{ title: 'Option 1', value: '1' }, { title: 'Option 2', value: '2' }]}
 *   placeholder="Choose an option"
 *   value="1"
 *   onChange={(value) => handleOptionChange(value)}
 *   error="Please select an option"
 * />
 */

import { Listbox, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { Fragment } from 'react'
import { Tooltip } from 'react-tooltip'
import QuestionIcon from '../assets/question.svg'

/**
 * @interface DropdownOption
 * @description Interface for the options of the dropdown.
 * @property {string} title - The title of the option.
 * @property {unknown} value - The value of the option.
 * @property {JSX.Element} icon - The icon of the option.
 */
interface DropdownOption {
    title: string
    value: unknown
    icon?: JSX.Element
}

/**
 * @interface DropdownProps
 * @description Props for the Dropdown component.
 * @property {string} placeholder - The placeholder text for the dropdown.
 * @property {string} label - The label text for the dropdown.
 * @property {string} name - The name of the dropdown.
 * @property {string} hint - The hint text for the dropdown.
 * @property {unknown} value - The current value of the dropdown.
 * @property {(...args: unknown[]) => void} onChange - The function to call when the dropdown value changes.
 * @property {DropdownOption[]} options - The options for the dropdown.
 * @property {boolean} disabled - Whether the dropdown is disabled.
 * @property {string} error - The error message to display if there is an error.
 */
interface DropdownProps {
    placeholder?: string
    label: string
    name: string
    hint?: string
    value?: unknown
    onChange: (...args: unknown[]) => void
    options: DropdownOption[]
    disabled?: boolean
    error?: string
}

/**
 * @function classNames
 * @description Utility function to join class names with spaces.
 * @param {...string} classes - The class names to join.
 * @returns {string} - The joined class names.
 */
function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

/**
 * @function Dropdown
 * @description Component for displaying a dropdown with label, options, and error handling.
 * @returns {JSX.Element | null} - The Dropdown component.
 */
export default function Dropdown(props: DropdownProps) {
    const { label, name, options = [], placeholder, value, disabled, onChange, error } = props
    const current = options.find((o) => o.value === value)

    return (
        <Listbox
            as="div"
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            className={classNames(
                'relative inline-block text-left min-w-fit',
                error ? 'border-red-600' : 'border-gray-300'
            )}
            value={value}
            disabled={disabled}
            onChange={onChange}
        >
            <div className="mb-1">
                <Listbox.Label className="flex font-medium text-sm text-gray-700">
                    <span>{label}</span>
                    {props.hint && (
                        <div className="relative flex justify-end grow">
                            <QuestionIcon
                                id={`${name}-tooltip`}
                                className="w-5 h-5 fill-gray-400 transition-colors cursor-pointer hover:fill-indigo-500"
                            />
                            <Tooltip anchorSelect={`#${name}-tooltip`}>
                                <p className="w-40 text-xs font-normal">{props.hint}</p>
                            </Tooltip>
                        </div>
                    )}
                </Listbox.Label>
            </div>
            <Listbox.Button
                className={classNames(
                    'inline-flex h-10 w-full justify-between rounded-md border focus:border-transparent appearance-none focus:outline-none px-3 py-2 text-sm text-gray-500 shadow-sm',
                    !disabled
                        ? 'bg-white hover:bg-gray-50 focus:ring-2 focus:ring-indigo-500'
                        : 'bg-gray-100',
                    current?.title ? 'font-medium' : 'font-regular',
                    error ? 'border-red-600' : 'border-gray-300'
                )}
            >
                {current ? (
                    <div className="flex items-center whitespace-nowrap text-ellipsis overflow-hidden">
                        {current.icon && <div className="mr-2">{current.icon}</div>}
                        <span>{current.title}</span>
                    </div>
                ) : (
                    <span>{placeholder}</span>
                )}
                <ChevronDownIcon className="-mr-1 ml-2 h-5 w-5" aria-hidden="true" />
            </Listbox.Button>

            {error && (
                <div className="mt-2 text-sm text-red-600">
                    <span>{error}</span>
                </div>
            )}

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Listbox.Options className="absolute left-0 z-10 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                        {options.map((option) => {
                            return (
                                <Listbox.Option key={option.title} value={option.value}>
                                    {({ active }) => (
                                        <div
                                            className={classNames(
                                                active
                                                    ? 'bg-gray-100 text-gray-900'
                                                    : 'text-gray-700',
                                                'w-full text-left px-4 py-2 text-sm flex flex-row gap-x-2'
                                            )}
                                        >
                                            {option.icon && (
                                                <div className="mr-2">{option.icon}</div>
                                            )}
                                            <span>{option.title}</span>
                                        </div>
                                    )}
                                </Listbox.Option>
                            )
                        })}
                    </div>
                </Listbox.Options>
            </Transition>
        </Listbox>
    )
}
